/** Flow 9 — Agenda, remises, and one study plan per deadline. */

import { dayAgendaUrl, taskAgendaUrl, weekAgendaUrl } from "./renderAgenda";

export const FLOW9_TITLE = "Flow 9 — Agenda et remises";

export const FLOW9_STEPS = [
  "Demande de photo",
  "Lecture de la photo",
  "Échéances reconstruites",
  "Plans d'étude",
  "Plans activés",
];

const TODAY = "2026-10-13";
const NAMES = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];

export const WEEK_ITEMS = [
  {
    id: "fr-redaction",
    subject: "Français",
    title: "Rédaction « Mon quartier »",
    kind: "devoir",
    date: "2026-10-14",
    detail: "en classe",
  },
  {
    id: "sci-labo",
    subject: "Sciences",
    title: "Rapport de labo",
    kind: "devoir",
    date: "2026-10-15",
    detail: "16 h",
  },
  {
    id: "math-exam",
    subject: "Maths",
    title: "Examen sur les fonctions",
    kind: "examen",
    date: "2026-10-16",
    detail: "9 h",
  },
];

export const DAY_ITEMS = [
  {
    id: "hist-questions",
    subject: "Histoire",
    title: "Questions p. 84",
    kind: "devoir",
    date: "2026-10-15",
    detail: "15 h",
  },
  WEEK_ITEMS[1],
];

export const TASK_ITEMS = [
  {
    id: "en-oral",
    subject: "Anglais",
    title: "Oral sur le roman",
    kind: "examen",
    date: "2026-10-19",
    detail: "10 h",
  },
];

export const ASK_MESSAGE =
  "Envoie une photo à la fois : l'agenda de ta semaine, une seule journée, ou la fiche d'un devoir à remettre.";

export const DEADLINES_MESSAGE =
  "Voici les échéances reconstruites, de la plus proche à la plus lointaine. Un devoir et un examen n'auront pas le même plan d'étude.";

export const OFFER_MESSAGE =
  "Je peux bâtir un plan d'étude différent pour chaque échéance, avec des séances avant la remise. On les construit ?";

export const PLANS_MESSAGE =
  "Chaque carte est un plan à part. L'examen a plus de séances que le devoir, et aucune séance n'est placée après l'heure de l'évaluation.";

export const READY_MESSAGE =
  "Ces plans sont prêts à devenir ton étude de la semaine. Ils restent séparés, un plan par remise.";

export const DONE_MESSAGE =
  "C'est activé. Tu as un plan pour chaque devoir et chaque examen, et tu peux encore envoyer une photo si une date manque.";

export const GAP_MESSAGE =
  "Envoie la photo suivante, une seule. J'ajouterai seulement les dates qui ne sont pas déjà là.";

export const BLUR_MESSAGE =
  "Je ne vois pas de date assez claire sur cette photo. Envoie l'agenda ou la fiche du devoir, une image à la fois.";

export const NUDGE_MESSAGE =
  "Pour cette démo, ajoute une photo ou choisis la semaine, la journée ou la fiche.";

export const COMPLETE_MESSAGE = "Parfait. Tes plans d'étude restent actifs.";

export function flow9DisplayIndex(flowStep) {
  if (flowStep <= 0 || flowStep === 90) return 0;
  if (flowStep === 1) return 1;
  if (flowStep === 2) return 2;
  if (flowStep === 3) return 3;
  return 4;
}

export const FLOW9_REPLIES = {
  ask: [
    { id: "f9-add", label: "Ajouter une photo", primary: true },
    { id: "f9-week", label: "Photo : semaine", primary: false },
    { id: "f9-day", label: "Photo : journée", primary: false },
    { id: "f9-task", label: "Photo : fiche", primary: false },
  ],
  offer: [
    { id: "f9-build", label: "Construire les plans", primary: true },
    { id: "f9-another", label: "Ajouter une autre photo", primary: false },
    { id: "f9-gap", label: "Une date manque", primary: false },
  ],
  ready: [
    { id: "f9-activate", label: "Activer les plans", primary: true },
    { id: "f9-another", label: "Ajouter une photo", primary: false },
  ],
  done: [
    { id: "f9-another", label: "Ajouter une photo", primary: true },
    { id: "f9-done", label: "C'est complet", primary: false },
  ],
};

function isoShift(iso, days) {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day + days);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${mm}-${dd}`;
}

export function dayLabel(iso) {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return `${NAMES[date.getDay()]} ${date.getDate()}`;
}

function byDate(a, b) {
  return a.date.localeCompare(b.date) || a.subject.localeCompare(b.subject);
}

export function mergeAgenda(current, incoming) {
  const seen = new Set(current.map((item) => item.id));
  const added = incoming.filter((item) => item && item.id && !seen.has(item.id));
  return { items: [...current, ...added].sort(byDate), added };
}

export function readMessage(source, added, total) {
  if (!added) {
    return "Je ne vois rien de nouveau à ajouter. Les échéances déjà reconstruites restent en place.";
  }
  if (source === "week") {
    return "Je vois l'agenda de ta semaine. Il y a la rédaction de français mercredi, le rapport de sciences jeudi, et l'examen de maths vendredi matin.";
  }
  if (source === "day" && added < DAY_ITEMS.length) {
    return "J'ajoute les questions d'histoire de jeudi. Le rapport de sciences était déjà noté, alors je ne le compte pas deux fois.";
  }
  if (source === "day") {
    return "Je vois ta journée de jeudi. Il y a le rapport de sciences avant 16 h et les questions d'histoire à 15 h.";
  }
  if (source === "task") {
    return "Je vois la fiche d'anglais. L'oral sur le roman est le lundi 19 octobre à 10 h, et il dure 5 minutes.";
  }
  const word = added > 1 ? "échéances" : "échéance";
  return `J'ajoute ${added} ${word} à partir de cette photo. Tu en as maintenant ${total}.`;
}

export function agendaCopy(field, ctx = {}) {
  if (field === "read") return readMessage(ctx.source, ctx.added, ctx.total);
  if (field === "ask") return ASK_MESSAGE;
  if (field === "deadlines") return DEADLINES_MESSAGE;
  if (field === "offer") return OFFER_MESSAGE;
  if (field === "plans") return PLANS_MESSAGE;
  if (field === "ready") return READY_MESSAGE;
  if (field === "done") return DONE_MESSAGE;
  if (field === "gap") return GAP_MESSAGE;
  if (field === "blur") return BLUR_MESSAGE;
  if (field === "complete") return COMPLETE_MESSAGE;
  return NUDGE_MESSAGE;
}

export function photoRows(source, addedCount) {
  if (source === "week") {
    return [
      ["Photo", "Semaine"],
      ["Période", "13–17 oct."],
      ["Repérés", "3 échéances"],
    ];
  }
  if (source === "day") {
    return [
      ["Photo", "Journée"],
      ["Jour", "Jeudi 15 oct."],
      ["Nouveautés", addedCount ? String(addedCount) : "Déjà connues"],
    ];
  }
  if (source === "task") {
    return [
      ["Photo", "Fiche"],
      ["Matière", "Anglais"],
      ["Remise", "Lun. 19 oct."],
    ];
  }
  return [
    ["Photo", "Import"],
    ["Nouveautés", String(addedCount || 0)],
  ];
}

export function deadlineRows(items) {
  return items.map((item) => [
    dayLabel(item.date),
    `${item.kind === "examen" ? "Examen" : "Devoir"} · ${item.subject}`,
  ]);
}

function planSessions(item) {
  const start = item.kind === "examen" ? 3 : 1;
  const endOffset = item.kind === "examen" ? 1 : 0;
  const days = [];
  for (let offset = start; offset >= endOffset; offset -= 1) {
    const day = isoShift(item.date, -offset);
    if (day >= TODAY && day <= item.date) days.push(day);
  }
  if (!days.length) days.push(item.date >= TODAY ? item.date : TODAY);
  const tasks =
    item.kind === "examen"
      ? ["Revoir la notion", "Faire des exercices", "Révision courte"]
      : ["Préparer le travail", "Terminer et relire"];
  const minutes = item.kind === "examen" ? [25, 25, 20] : [20, 20];
  return days.map((day, index) => ({
    when: dayLabel(day),
    task: tasks[Math.min(index, tasks.length - 1)],
    minutes: minutes[Math.min(index, minutes.length - 1)],
  }));
}

export function buildStudyPlans(items) {
  return items.map((item) => {
    const sessions = planSessions(item);
    const kind = item.kind === "examen" ? "Examen" : "Devoir";
    return {
      subject: item.subject,
      title: `Plan ${item.subject}`,
      subtitle: `${kind} · ${dayLabel(item.date)} · ${item.detail || item.title}`,
      minutes: sessions.reduce((sum, session) => sum + session.minutes, 0),
      rows: sessions.map((session) => [session.when, `${session.task} · ${session.minutes} min`]),
    };
  });
}

export function planTotals(plans) {
  return {
    sessions: plans.reduce((sum, plan) => sum + plan.rows.length, 0),
    minutes: plans.reduce((sum, plan) => sum + plan.minutes, 0),
    label: plans.length > 1 ? `${plans.length} plans` : plans.length === 1 ? "1 plan" : "—",
  };
}

export function itemsFor(source) {
  if (source === "week") return WEEK_ITEMS;
  if (source === "day") return DAY_ITEMS;
  if (source === "task") return TASK_ITEMS;
  return [];
}

export function agendaSrc(source) {
  if (source === "day") return dayAgendaUrl();
  if (source === "task") return taskAgendaUrl();
  return weekAgendaUrl();
}

function result(messages, quickReplies, flowStep, items, planReady = false) {
  return {
    messages,
    quickReplies,
    flowStep,
    items,
    planReady,
    status: FLOW9_STEPS[flow9DisplayIndex(flowStep)],
  };
}

export function buildFlow9Snapshot(displayIndex) {
  const step = Math.min(Math.max(displayIndex, 0), FLOW9_STEPS.length - 1);
  const messages = [{ sender: "agent", messageKey: "f9.ask", text: ASK_MESSAGE }];
  if (step === 0) return result(messages, FLOW9_REPLIES.ask, 0, []);

  const items = WEEK_ITEMS;
  messages.push({ sender: "user", kind: "photo", src: weekAgendaUrl(), alt: "Photo de l'agenda" });
  messages.push({ sender: "agent", messageKey: "f9.read", text: readMessage("week", 3, 3) });
  messages.push({ sender: "agent", kind: "agenda", rows: photoRows("week", 3) });
  if (step === 1) return result(messages, FLOW9_REPLIES.ask, 1, items);

  messages.push({ sender: "agent", messageKey: "f9.deadlines", text: DEADLINES_MESSAGE });
  messages.push({ sender: "agent", kind: "deadlines", rows: deadlineRows(items) });
  messages.push({ sender: "agent", messageKey: "f9.offer", text: OFFER_MESSAGE });
  if (step === 2) return result(messages, FLOW9_REPLIES.offer, 2, items);

  const plans = buildStudyPlans(items);
  messages.push({ sender: "agent", messageKey: "f9.plans", text: PLANS_MESSAGE });
  plans.forEach((plan) => {
    messages.push({
      sender: "agent",
      kind: "studyplan",
      title: plan.title,
      subtitle: plan.subtitle,
      rows: plan.rows,
      minutes: plan.minutes,
    });
  });
  messages.push({ sender: "agent", messageKey: "f9.ready", text: READY_MESSAGE });
  if (step === 3) return result(messages, FLOW9_REPLIES.ready, 3, items);

  messages.push({ sender: "user", text: "Activer les plans" });
  messages.push({ sender: "agent", messageKey: "f9.done", text: DONE_MESSAGE });
  return result(messages, FLOW9_REPLIES.done, 5, items, true);
}
